/**
 * Time helpers. The business operates in **Asia/Dubai** (fixed UTC+4, no DST),
 * and every surface in the app renders times in that zone. Admins, however,
 * book jobs with a browser-native `datetime-local` input which reads/writes in
 * the *admin's* local timezone. If the admin's machine isn't on Dubai time,
 * the entered wall-clock time gets stored offset-by-the-difference and the
 * worker sees the wrong time. These helpers bridge that gap by always treating
 * the `datetime-local` value as Asia/Dubai wall time.
 */

export const DUBAI_TZ = 'Asia/Dubai';
/** Fixed Dubai offset in minutes (UTC+4, no daylight saving). */
const DUBAI_OFFSET_MIN = 4 * 60;

/**
 * Convert an ISO instant into the `YYYY-MM-DDTHH:MM` string a
 * `<input type="datetime-local">` expects, expressed in Asia/Dubai wall time.
 */
export function isoToDubaiLocal(iso: string): string {
  const shifted = new Date(new Date(iso).getTime() + DUBAI_OFFSET_MIN * 60_000);
  // The UTC fields of the shifted instant equal Dubai wall-clock fields.
  return shifted.toISOString().slice(0, 16);
}

/**
 * Convert a `datetime-local` value (`YYYY-MM-DDTHH:MM`, optionally with
 * seconds) entered as Asia/Dubai wall time into a UTC ISO instant.
 */
export function dubaiLocalToISO(local: string): string {
  const trimmed = local.length === 16 ? `${local}:00` : local;
  return new Date(`${trimmed}+04:00`).toISOString();
}

/** Just the `YYYY-MM-DD` date portion of an ISO instant, in Asia/Dubai. */
export function isoToDubaiDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: DUBAI_TZ });
}
